import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../utils/supabaseStorage';
import { useAppStore } from '../store/useAppStore';
import { FallbackScreen } from '../components/FallbackScreen';
import { logAnalytics } from '../utils/database';
import { AlertCircle, Headphones } from 'lucide-react';
import { DynamicWorkflowForm } from '../components/forms/DynamicWorkflowForm';
import { ModalContainer } from '../components/ModalContainer';
import { CheckCircle, Eye, Download } from 'lucide-react';
import { CachedImage } from '../components/CachedImage';
import { getAssetMediaType } from '../utils/formPreviewHelpers';
import { MediaPreviewModal } from '../components/MediaPreviewModal';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { PublicAgentChat } from '../components/public/PublicAgentChat';
import type { AgentTemplate } from '../types/agent';

interface PageConfig {
  id: string;
  route_path: string;
  page_title?: string;
  page_description?: string;
  example_media_url?: string;
  example_media_type?: 'image' | 'video' | 'audio';
  app_id: string;
  api_key_id?: string;
  modelconfig_id?: string | null;
  agent_template_id?: string | null;
  hy_user_id?: string;
  created_at: string;
  updated_at: string;
}

interface ModelConfig {
  id: string;
  model_id: string;
  name: string;
  description: string;
  category: string;
  subcategory?: string;
  icon_name: string;
  icon_color: string;
  icon_bg: string;
  tags?: string[];
  fal_endpoint: string;
  credit_cost: number;
  output_type: 'image' | 'video' | 'audio';
  estimated_time?: string;
  is_available: boolean;
  coming_soon: boolean;
  featured: boolean;
  route?: string;
  input_schema: any[];
  output_mapping: any;
  sort_order?: number;
  fal_stream_node_id?: string;
  fal_polling_node_name?: string;
  is_long_running?: boolean;
  polling_interval_seconds?: number;
  created_at?: string;
  updated_at?: string;
  hy_user_id?: string;
}

const PublicModelPage = () => {
  const { routePath } = useParams<{ routePath: string }>();
  const { setSession: setGlobalSession, session: storeSession } = useAppStore();

  const [session, setSession] = useState<any>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authCompleted, setAuthCompleted] = useState(false);

  const [pageConfig, setPageConfig] = useState<PageConfig | null>(null);
  const [modelConfig, setModelConfig] = useState<ModelConfig | null>(null);
  const [agentTemplate, setAgentTemplate] = useState<AgentTemplate | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);
  const [generatedAssets, setGeneratedAssets] = useState<any[]>([]);
  const [previewAsset, setPreviewAsset] = useState<any>(null);

  // 1. Fetch Page and Model Configuration
  useEffect(() => {
    const fetchConfigurations = async () => {
      if (!routePath) {
        setConfigError('Route path is missing.');
        setConfigLoading(false);
        return;
      }

      try {
        setConfigLoading(true);
        setConfigError(null);

        console.log('📄 [PUBLIC-MODEL-PAGE] Loading page config for route:', routePath);

        // Fetch page configuration
        const { data: pageData, error: pageError } = await supabase
          .from('pages')
          .select('*')
          .eq('route_path', `/${routePath}`)
          .maybeSingle();

        if (pageError) {
          setConfigError(pageError.message);
          setPageConfig(null);
          setModelConfig(null);
          return;
        }

        if (!pageData) {
          setConfigError('Page not found. This public page does not exist.');
          setPageConfig(null);
          setModelConfig(null);
          return;
        }

        console.log('📄 [PUBLIC-MODEL-PAGE] Page config loaded:', pageData);
        if (!pageData.app_id?.trim()) {
          setConfigError('This page has no App ID — set it in Admin → Page Settings.');
          setPageConfig(null);
          setModelConfig(null);
          setAgentTemplate(null);
          return;
        }
        setPageConfig(pageData);
        setAgentTemplate(null);

        if (pageData.agent_template_id) {
          const { data: atData, error: atError } = await supabase
            .from('agent_templates')
            .select('*')
            .eq('id', pageData.agent_template_id)
            .eq('is_active', true)
            .single();

          if (atError || !atData) {
            setConfigError('Agent configuration not found or inactive.');
            setModelConfig(null);
            setAgentTemplate(null);
            return;
          }

          setAgentTemplate(atData as AgentTemplate);
          setModelConfig(null);
        } else if (pageData.modelconfig_id) {
          const { data: modelData, error: modelError } = await supabase
            .from('modelconfig')
            .select('*')
            .eq('id', pageData.modelconfig_id)
            .single();

          if (modelError) {
            setConfigError('Associated model configuration not found.');
            setModelConfig(null);
            return;
          }

          console.log('🤖 [PUBLIC-MODEL-PAGE] Model config loaded:', modelData);
          setModelConfig(modelData);
        } else {
          setConfigError('This page is not linked to a model or agent.');
          setModelConfig(null);
          setAgentTemplate(null);
        }

      } catch (err) {
        setConfigError(err instanceof Error ? err.message : 'Failed to load page configuration.');
        setPageConfig(null);
        setModelConfig(null);
        setAgentTemplate(null);
      } finally {
        setConfigLoading(false);
      }
    };

    fetchConfigurations();
  }, [routePath]);

  // 2. Sync Supabase Auth session from global store (route uses RequireAuth — any signed-in user, not staff-only)
  useEffect(() => {
    if (configLoading || !pageConfig) {
      return;
    }
    const isAgentPage = !!pageConfig.agent_template_id;
    if (isAgentPage && !agentTemplate) {
      return;
    }
    if (!isAgentPage && !modelConfig) {
      return;
    }

    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        setAuthError(null);

        await logAnalytics('public_model_page_access', {
          route_path: routePath,
          app_id: pageConfig.app_id,
          model_id: modelConfig?.model_id,
          timestamp: new Date().toISOString(),
          url: window.location.href
        });

        if (!storeSession) {
          await logAnalytics('public_model_page_session_missing', {
            route_path: routePath,
            app_id: pageConfig.app_id,
            error: 'No Supabase session',
          });
          setAuthError('Please sign in to use this page.');
          setIsAuthenticated(false);
          return;
        }

        setSession(storeSession);
        setIsAuthenticated(true);
        setGlobalSession(storeSession);

        await logAnalytics('public_model_page_session_established', {
          route_path: routePath,
          app_id: pageConfig.app_id,
          model_id: modelConfig?.model_id,
          user_id: storeSession.hy_user_id,
          plan: storeSession.hy_plan_dev_name
        });
      } catch (error) {
        console.error('Public model page auth error:', error);
        await logAnalytics('public_model_page_auth_error', {
          route_path: routePath,
          app_id: pageConfig?.app_id,
          model_id: modelConfig?.model_id,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
        setAuthError('Failed to authenticate - Please try again');
      } finally {
        setIsLoading(false);
        setAuthCompleted(true);
      }
    };

    void initializeAuth();
  }, [configLoading, pageConfig, modelConfig, agentTemplate, routePath, setGlobalSession, storeSession]);

  // Handle form completion
  const handleFormComplete = () => {
    console.log('🎉 [PUBLIC-MODEL-PAGE] Model generation completed!');
  };

  // Handle generation error
  const handleGenerationError = (error: string) => {
    console.log('❌ [PUBLIC-MODEL-PAGE] Generation error:', error);
    const { openErrorModal } = useAppStore.getState();
    openErrorModal('Generation Failed', error);
  };

  const handlePreviewAsset = (asset: any) => {
    setPreviewAsset(asset);
  };

  const handleDownloadAsset = (asset: any) => {
    const link = document.createElement('a');
    link.href = asset.public_url;
    link.download = asset.filename || `generated-${asset.id}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderAssetPreview = (asset: any) => {
    if (asset.status === 'processing') {
      return (
        <div className="w-full min-h-[280px] flex flex-col items-center justify-center bg-elevated p-8 gap-4">
          <LoadingSpinner size="lg" />
          <p className="text-th-secondary text-sm text-center max-w-sm">
            {asset.message ||
              'This item is in your library as a placeholder. When FAL finishes, it calls our webhook and the preview updates here — open Assets to watch it complete.'}
          </p>
        </div>
      );
    }

    const mediaType = getAssetMediaType(asset);
    
    switch (mediaType) {
      case 'image':
        return (
          <div className="w-full max-h-[calc(100vh-350px)] flex items-center justify-center bg-black">
            <CachedImage
              src={asset.public_url}
              alt="Generated content"
              className="max-w-full max-h-full object-contain"
              style={{
                maxHeight: 'calc(100vh - 350px)',
                width: 'auto',
                height: 'auto'
              }}
              fallback={
                <div className="w-full h-32 flex items-center justify-center text-th-secondary bg-elevated rounded-lg">
                  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                  </svg>
                </div>
              }
            />
          </div>
        );
      
      case 'video':
        return (
          <div className="w-full max-h-[calc(100vh-350px)] bg-black flex items-center justify-center">
            <video
              src={asset.public_url}
              className="max-w-full max-h-full object-contain"
              style={{
                maxHeight: 'calc(100vh - 350px)',
                width: 'auto',
                height: 'auto'
              }}
              controls
              preload="metadata"
              onError={(e) => {
                const target = e.target as HTMLVideoElement;
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent) {
                  parent.innerHTML = '<div class="w-full h-full flex items-center justify-center text-th-secondary"><svg class="w-8 h-8" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg></div>';
                }
              }}
            />
          </div>
        );
      
      case 'audio':
        return (
          <div className="w-full max-h-[calc(100vh-350px)] bg-status-warning/20 rounded-lg p-6 text-center flex flex-col items-center justify-center">
            <svg className="w-12 h-12 text-status-warning mx-auto mb-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
            </svg>
            <audio
              src={asset.public_url}
              controls
              className="w-full bg-elevated rounded-lg"
              onError={(e) => {
                const target = e.target as HTMLAudioElement;
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent) {
                  parent.innerHTML = '<div class="text-center text-th-secondary">Audio unavailable</div>';
                }
              }}
            />
          </div>
        );
      
      default:
        return (
          <div className="w-full max-h-[calc(100vh-350px)] bg-elevated rounded-lg flex items-center justify-center">
            <svg className="w-8 h-8 text-th-secondary" fill="currentColor" viewBox="0 0 24 24">
              <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
            </svg>
          </div>
        );
    }
  };

  // Show loading state during initial authentication and config fetch
  if (configLoading || isLoading || !authCompleted) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <img
            src="https://qsiwkqsdtbfftmxbnhie.supabase.co/storage/v1/object/public/public/c599a7b3-23d3-4f3a-a44b-b1801b7cdf24.gif"
            alt="Loading..."
            className="w-[500px] h-[500px] mx-auto rounded-lg"
          />
        </div>
      </div>
    );
  }

  // Show fallback if authentication failed
  if (authError || !isAuthenticated || !session) {
    return <FallbackScreen />;
  }

  const isAgentPage = !!(pageConfig?.agent_template_id && agentTemplate);

  // Render error states for page/model config
  if (configError || !pageConfig || (!modelConfig && !isAgentPage)) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-elevated border border-subtle rounded-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-status-error mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-th-primary mb-2">
            Page Not Found
          </h1>
          <p className="text-th-secondary mb-6">
            {configError || 'Could not load the page or model configuration.'}
          </p>
          <button
            onClick={() => window.history.back()}
            className="button-primary"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  console.log('🆔 [PUBLIC-MODEL-PAGE] Final app_id being passed to form:', pageConfig.app_id);

  if (isAgentPage && agentTemplate && session) {
    return (
      <PublicAgentChat
        pageConfig={{
          id: pageConfig.id,
          route_path: pageConfig.route_path,
          page_title: pageConfig.page_title,
          page_description: pageConfig.page_description,
          app_id: pageConfig.app_id,
        }}
        template={agentTemplate}
        session={session}
      />
    );
  }

  if (!modelConfig) {
    return null;
  }

  return (
    <>
      <div className="h-screen bg-base flex flex-col">
      {/* Mobile Layout - Full Screen Form */}
      <div className="lg:hidden flex-1 flex flex-col h-full">
        <DynamicWorkflowForm
          model={modelConfig as any}
          creditCost={modelConfig.credit_cost}
          pageTitle={pageConfig.page_title || modelConfig.name}
          pageDescription={pageConfig.page_description || modelConfig.description}
          exampleMediaUrl={pageConfig.example_media_url}
          exampleMediaType={pageConfig.example_media_type}
          apiKeyId={pageConfig.api_key_id}
          appId={pageConfig.app_id}
          onCancel={() => window.history.back()}
          onComplete={handleFormComplete}
          onError={handleGenerationError}
          onAssetsGenerated={(assets) => setGeneratedAssets(assets)}
        />
      </div>

      {/* Desktop Main Content Area */}
      <div className="flex-1 flex flex-col lg:flex-row min-h-0 h-full">
        {/* Form Section - Desktop: Left side, Mobile: Full width */}
        <div className={`${pageConfig.example_media_url ? 'lg:w-1/2 xl:w-2/5 lg:border-r lg:border-subtle' : 'lg:w-full'} flex flex-col min-h-0 h-full`}>
          <DynamicWorkflowForm
            model={modelConfig as any}
            creditCost={modelConfig.credit_cost}
            pageTitle={pageConfig.page_title || modelConfig.name}
            pageDescription={pageConfig.page_description || modelConfig.description}
            exampleMediaUrl={pageConfig.example_media_url}
            exampleMediaType={pageConfig.example_media_type}
            apiKeyId={pageConfig.api_key_id}
            appId={pageConfig.app_id}
            onCancel={() => window.history.back()}
            onComplete={handleFormComplete}
            onError={handleGenerationError}
            onAssetsGenerated={(assets) => setGeneratedAssets(assets)}
          />
        </div>

        {/* Example Media Section - Desktop only, right side */}
        {pageConfig.example_media_url && (
          <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 flex-col min-h-0">
            <div className="flex-1 flex items-center justify-center bg-black relative">
              {/* Show generated assets if available, otherwise show example media */}
              {generatedAssets.length > 0 ? (
                <div className="w-full h-full overflow-y-auto p-4">
                  <div className="space-y-4">
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-semibold text-th-primary flex items-center justify-center space-x-2">
                        <CheckCircle className="w-6 h-6 text-status-success" />
                        <span>Generated Content ({generatedAssets.length})</span>
                      </h3>
                    </div>
                    <div className="space-y-4">
                      {generatedAssets.map((asset, index) => (
                        <div key={index} className="bg-elevated rounded-lg overflow-hidden border border-subtle hover:border-brand/50 transition-all group">
                          <div className="relative">
                            {renderAssetPreview(asset)}
                            
                            {/* Hover Actions */}
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                              <div className="flex space-x-3">
                                <button
                                  onClick={() => handlePreviewAsset(asset)}
                                  className="p-2 bg-white/20 hover:bg-white/30 rounded-full backdrop-blur-sm transition-all"
                                  title="Preview"
                                >
                                  <Eye className="w-4 h-4 text-white" />
                                </button>
                                <button
                                  onClick={() => handleDownloadAsset(asset)}
                                  className="p-2 bg-white/20 hover:bg-white/30 rounded-full backdrop-blur-sm transition-all"
                                  title="Download"
                                >
                                  <Download className="w-4 h-4 text-white" />
                                </button>
                              </div>
                            </div>
                          </div>
                          
                          {/* Asset Info */}
                          <div className="p-3 flex items-center justify-between">
                            <div className="flex flex-col">
                              <span className="text-th-secondary text-xs">
                                {getAssetMediaType(asset).toUpperCase()}
                              </span>
                              <span
                                className={
                                  asset.status === 'processing'
                                    ? 'text-status-warning text-xs'
                                    : 'text-status-success text-xs'
                                }
                              >
                                {asset.status === 'processing' ? 'Processing' : 'Ready'}
                              </span>
                            </div>
                            {asset.status !== 'processing' && asset.public_url ? (
                              <button
                                onClick={() => handleDownloadAsset(asset)}
                                className="flex items-center space-x-1 px-3 py-1 bg-brand/20 text-brand rounded-full text-xs font-medium hover:bg-brand/30 transition-all"
                              >
                                <Download className="w-3 h-3" />
                                <span>Save</span>
                              </button>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                // Show example media when no generated content
                <>
              {pageConfig.example_media_type === 'image' && (
                <img
                  src={pageConfig.example_media_url}
                  alt="Example output"
                  className="max-w-full max-h-full object-contain"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      parent.innerHTML = '<div class="text-center text-th-secondary"><svg class="w-16 h-16 mx-auto mb-4" fill="currentColor" viewBox="0 0 24 24"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg><p>Example image unavailable</p></div>';
                    }
                  }}
                />
              )}
              
              {pageConfig.example_media_type === 'video' && (
                <video
                  src={pageConfig.example_media_url}
                  className="max-w-full max-h-full object-contain"
                  controls
                  autoPlay
                  muted
                  loop
                  playsInline
                  onError={(e) => {
                    const target = e.target as HTMLVideoElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      parent.innerHTML = '<div class="text-center text-th-secondary"><svg class="w-16 h-16 mx-auto mb-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg><p>Example video unavailable</p></div>';
                    }
                  }}
                />
              )}
              
              {pageConfig.example_media_type === 'audio' && (
                <div className="w-full max-w-md space-y-6">
                  <div className="w-32 h-32 bg-status-warning/30 rounded-full flex items-center justify-center mx-auto">
                    <Headphones className="w-16 h-16 text-status-warning" />
                  </div>
                  <audio
                    src={pageConfig.example_media_url}
                    className="w-full bg-elevated rounded-lg p-3"
                    controls
                    preload="metadata"
                    onError={(e) => {
                      const target = e.target as HTMLAudioElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = '<div class="text-center text-th-secondary">Example audio unavailable</div>';
                      }
                    }}
                  />
                </div>
              )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Media Preview Modal */}
      {previewAsset && (
        <MediaPreviewModal
          isOpen={!!previewAsset}
          onClose={() => setPreviewAsset(null)}
          asset={previewAsset}
          onDownload={() => handleDownloadAsset(previewAsset)}
        />
      )}

      {/* Background Effects */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute top-3/4 left-1/2 w-80 h-80 bg-status-warning/5 rounded-full blur-3xl" />
      </div>
      </div>

      {/* Modal Container for insufficient credits and other modals */}
      <ModalContainer />
    </>
  );
};

export default PublicModelPage;